/**
 * A square button that shows only an icon: close, clear, copy, more. The `label` is required, so
 * an icon button without a name is a type error rather than an axe finding. It is the button's
 * accessible name and the tip a sighted person sees on hover and focus.
 *
 * States and behaviour are Button's: hover, pressed, focus ring, disabled with a reason, loading
 * with a spinner in place of the icon.
 */
import * as stylex from "@stylexjs/stylex";
import type { MouseEvent, ReactNode } from "react";
import { useId } from "react";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import type { ButtonCorners } from "./buttonStyle.ts";
import { buttonStyle } from "./buttonStyle.ts";
import { focusRing } from "./focus.ts";
import { Spinner } from "./Spinner.tsx";
import { Tooltip } from "./Tooltip.tsx";
const styles = {
  square: {
    kg3NbH: "xnjsko4",
    kmuXW: "x2lah0s",
    $$css: true
  },
  sm: {
    kzqmXN: "x150l1ig xyzrq1n x1kpfe0p",
    kAzted: "xn2nem0 xlivvtk xei8rr2",
    $$css: true
  },
  md: {
    kzqmXN: "x1uf7ocq",
    kAzted: "x1envrbd",
    $$css: true
  }
};
export interface IconButtonProps {
  /** What the button does, as a verb: "Clear search", "Copy order id". */
  readonly label: string;
  readonly icon: ReactNode;
  readonly onClick?: () => void;
  readonly variant?: "secondary" | "tertiary" | "dangerSecondary";
  readonly size?: "sm" | "md";
  readonly corners?: ButtonCorners;
  readonly loading?: boolean;
  readonly disabled?: boolean;
  readonly disabledReason?: string;
  readonly pressed?: boolean;
  /** Lines the tip up with the button's end, for a button at the right edge of a row. */
  readonly tipAlign?: "start" | "end";
  /** More the tip says than the name does, such as the whole of a value the screen shows cut. */
  readonly tip?: string;
  readonly testId?: string | undefined;
}
export function IconButton({
  label,
  icon,
  onClick,
  variant = "tertiary",
  size: step = "md",
  corners = "rounded",
  loading = false,
  disabled = false,
  disabledReason,
  pressed,
  tipAlign = "start",
  tip,
  testId
}: IconButtonProps) {
  const tipId = useId();
  const inert = loading || disabled;
  const reason = disabled ? disabledReason : undefined;
  const press = (event: MouseEvent<HTMLButtonElement>) => {
    if (inert) event.preventDefault();else onClick?.();
  };
  return <Tooltip id={tipId} text={reason !== undefined ? `${label}. ${reason}` : tip === undefined ? label : `${label}: ${tip}`} align={tipAlign}>
      <button type="button" aria-label={label} aria-describedby={reason === undefined ? undefined : tipId} aria-disabled={inert || undefined} aria-busy={loading || undefined} aria-pressed={pressed} onClick={press} data-testid={testId} {...stylex.props(buttonStyle.base, corners === "pill" ? buttonStyle.pill : buttonStyle.rounded, styles.square, styles[step], buttonStyle[variant], pressed === true && buttonStyle.pressed, disabled && buttonStyle.disabled, loading && buttonStyle.loading, focusRing)}>
        <span {...stylex.props(buttonStyle.slot)}>{loading ? <Spinner /> : icon}</span>
      </button>
    </Tooltip>;
}